import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Spinner, Alert, Modal, ListGroup } from 'react-bootstrap';
import { FaMoneyBillWave, FaPlus, FaTrash, FaCheckCircle, FaExclamationTriangle, FaFingerprint } from 'react-icons/fa';
import api from '../services/api';

const ComprehensiveDMTModal = ({ show, onHide, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Query Remitter, 2: E-KYC, 3: Register, 4: Add Beneficiary, 5: Transaction
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fingerprint capture state
  const [fingerprintStatus, setFingerprintStatus] = useState('idle'); // idle, capturing, captured, verified, failed
  const [pidData, setPidData] = useState('');

  // Remitter Data
  const [remitterData, setRemitterData] = useState({
    mobile: '',
    name: '',
    aadhar: '',
    aadhar_auto_filled: false, // Track if Aadhar was auto-filled from query response
    pincode: '',
    address: '',
    is_iris: 2, // Default to Finger (2)
    accessmode: 'WEB', // Default to WEB (required for Android app: use 'APP')
    otp: '',
    stateresp: '',
    ekyc_id: '',
    otp_ref_number: '',
    pid_data: '' // For fingerprint data
  });

  // Beneficiary Data
  const [beneficiaryData, setBeneficiaryData] = useState({
    benename: '',
    accno: '',
    ifsccode: '',
    bankid: '',
    verified: '0'
  });

  // Transaction Data
  const [transactionData, setTransactionData] = useState({
    amount: '',
    bene_id: '',
    otp: '',
    reference_id: '',
    stateresp: '' // Required for process transaction (from send-otp response)
  });

  // Refund Data
  const [refundData, setRefundData] = useState({
    reference_id: '',
    otp: ''
  });

  // State Management
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  // Quick amounts for transaction
  const quickAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 25000];

  // Load E-KYC data from localStorage when modal opens or mobile changes
  // IMPORTANT: Only load E-KYC data for registered remitters (response_code 1)
  // For unregistered numbers, E-KYC data will be cleared and fresh KYC is required
  // Using localStorage instead of sessionStorage for persistence across logins
  useEffect(() => {
    if (remitterData.mobile && remitterData.mobile.length === 10) {
      const storageKey = `dmt_ekyc_${remitterData.mobile}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const ekycData = JSON.parse(stored);
          // Check if E-KYC data is still valid (within 30 minutes as per PaySprint requirement)
          const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
          const isExpired = ekycData.timestamp && (Date.now() - ekycData.timestamp) > thirtyMinutes;
          
          if (isExpired) {
            // E-KYC data expired - clear it
            localStorage.removeItem(storageKey);
            console.log('E-KYC data expired (older than 30 minutes). Fresh E-KYC required.');
          } else if (!remitterData.ekyc_id && !remitterData.stateresp && ekycData.ekyc_id && ekycData.stateresp) {
            // Only load E-KYC data if remitter is registered (we'll verify this in queryRemitter)
            // For now, load it but it will be cleared if remitter is unregistered
            // E-KYC data is valid - load it (will be cleared if unregistered)
            setRemitterData(prev => ({
              ...prev,
              ekyc_id: ekycData.ekyc_id,
              stateresp: ekycData.stateresp
            }));
          }
        } catch (e) {
          console.error('Failed to load E-KYC data from localStorage:', e);
        }
      }
    }
  }, [remitterData.mobile]);

  // Save E-KYC data to localStorage when ekyc_id and stateresp are set
  // Using localStorage for persistence across browser sessions/logins
  useEffect(() => {
    if (remitterData.mobile && remitterData.mobile.length === 10 && remitterData.ekyc_id && remitterData.stateresp) {
      const storageKey = `dmt_ekyc_${remitterData.mobile}`;
      localStorage.setItem(storageKey, JSON.stringify({
        ekyc_id: remitterData.ekyc_id,
        stateresp: remitterData.stateresp,
        timestamp: Date.now()
      }));
    }
  }, [remitterData.mobile, remitterData.ekyc_id, remitterData.stateresp]);

  // Auto-fetch beneficiaries when entering Step 4
  useEffect(() => {
    if (currentStep === 4 && remitterData.mobile) {
      // Fetch beneficiaries when entering beneficiary management step (silent - no message)
      fetchBeneficiaries(false);
    }
  }, [currentStep, remitterData.mobile]);

  // Load beneficiaries from localStorage when modal opens or mobile changes
  // Using localStorage for persistence across browser sessions/logins
  useEffect(() => {
    if (remitterData.mobile && remitterData.mobile.length === 10) {
      const storageKey = `dmt_beneficiaries_${remitterData.mobile}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const storedBeneficiaries = JSON.parse(stored);
          // Only load if we don't already have beneficiaries
          if (beneficiaries.length === 0 && storedBeneficiaries.length > 0) {
            setBeneficiaries(storedBeneficiaries);
          }
        } catch (e) {
          console.error('Failed to load beneficiaries from sessionStorage:', e);
        }
      }
    }
  }, [remitterData.mobile]);

  // Capture fingerprint from RD device (FM220U)
  const captureFingerprint = async () => {
    setFingerprintStatus('capturing');
    setError('');
    setSuccess('Please place your finger on the biometric device...');

    try {
      // Call RD Service to capture fingerprint from FM220U device
      const rdServiceUrl = 'https://127.0.0.1:11200/rd/capture';
      
      const pidOptions = `<PidOptions ver='1.0'>
        <Opts 
          fCount='1' 
          fType='0' 
          iCount='0' 
          iType='0' 
          pCount='0' 
          pType='0' 
          format='0' 
          pidVer='2.0' 
          timeout='25000' 
          otp=''
        />
      </PidOptions>`;

      // Use XMLHttpRequest for custom CAPTURE method
      const pidDataXml = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('CAPTURE', rdServiceUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/xml');
        
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(xhr.responseText);
          } else {
            reject(new Error(`RD Service error: ${xhr.status} ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Failed to connect to RD Service. Please ensure RD Service is running and device is connected.'));
        };
        
        xhr.send(pidOptions);
      });

      // Check if capture was successful (errCode="0")
      if (pidDataXml.includes('errCode="0"') || pidDataXml.includes('errCode=\'0\'')) {
        // Extract quality score for verification
        const qualityMatch = pidDataXml.match(/qScore="(\d+)"/);
        const qualityScore = qualityMatch ? parseInt(qualityMatch[1]) : 0;
        
        if (qualityScore >= 50) {
          setPidData(pidDataXml);
          setRemitterData(prev => ({ ...prev, pid_data: pidDataXml }));
          setFingerprintStatus('captured');
          setSuccess(`Fingerprint captured successfully (Quality: ${qualityScore}%). PID Data ready for E-KYC.`);
          
          // Simulate verification
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          setFingerprintStatus('verified');
          setSuccess('Fingerprint verified successfully. You can now proceed with E-KYC.');
        } else {
          setFingerprintStatus('failed');
          setError(`Fingerprint quality too low (${qualityScore}%). Please try again with a clean finger.`);
        }
      } else {
        // Extract error code and message
        const errorMatch = pidDataXml.match(/errCode="(\d+)"|errCode='(\d+)'/);
        const errorCode = errorMatch ? (errorMatch[1] || errorMatch[2]) : 'unknown';
        const errorInfoMatch = pidDataXml.match(/errInfo="([^"]+)"|errInfo='([^']+)'/);
        const errorInfo = errorInfoMatch ? (errorInfoMatch[1] || errorInfoMatch[2]) : 'Unknown error';
        
        setFingerprintStatus('failed');
        setError(`Fingerprint capture failed (Error ${errorCode}: ${errorInfo}). Please try again.`);
      }
    } catch (error) {
      setFingerprintStatus('failed');
      const errorMessage = error.message || 'Fingerprint capture failed. Please ensure RD Service is running and device is connected.';
      setError(errorMessage);
      console.error('Fingerprint capture error:', error);
    }
  };

  // Step 1: Query Remitter
  const handleQueryRemitter = async () => {
    if (!remitterData.mobile || remitterData.mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt/remitter/query', {
        mobile: remitterData.mobile
      });

      if (response.data.success) {
        // Parse response_code - can be number or string
        const responseCode = parseInt(response.data.data?.response_code ?? response.data.response_code ?? 0);
        const responseData = response.data.data?.data || response.data.data || {};
        
        console.log('🔍 Query Remitter Response:', {
          responseCode,
          responseData,
          fullResponse: response.data
        });
        
        if (responseCode === 1) {
          // Remitter account details fetched - already registered
          // Extract Aadhar number from response and auto-fill it
          // Then go to E-KYC step to capture fingerprint
          const aadharFromResponse = responseData?.aadhar || 
                                     responseData?.aadhaar_number || 
                                     responseData?.aadhar_number || 
                                     '';
          
          console.log('✅ Registered remitter found. Aadhar from response:', aadharFromResponse);
          
          // Auto-fill Aadhar from query response (if available)
          const isAadharAutoFilled = !!aadharFromResponse && aadharFromResponse.length === 12;
          
          setRemitterData(prev => ({
            ...prev,
            aadhar: aadharFromResponse,
            aadhar_auto_filled: isAadharAutoFilled // Mark as auto-filled only if valid Aadhar was found
          }));
          
          if (isAadharAutoFilled) {
            setSuccess('Remitter found! Aadhar number auto-filled. Please capture fingerprint to continue.');
          } else {
            setSuccess('Remitter found! Please enter your Aadhar number and capture fingerprint to continue.');
          }
          setCurrentStep(2); // Go to E-KYC step to capture fingerprint
        } else if (responseCode === 2) {
          // Please do remitter e-kyc - UNREGISTERED NUMBER
          // CRITICAL: For unregistered numbers, always require fresh KYC
          // Clear any old E-KYC data from localStorage to prevent skipping KYC
          const storageKey = `dmt_ekyc_${remitterData.mobile}`;
          localStorage.removeItem(storageKey);
          
          // Clear E-KYC data and Aadhar from state to ensure fresh KYC is required
          // User must enter Aadhar number manually for new remitters
          setRemitterData(prev => ({
            ...prev,
            aadhar: '', // Clear Aadhar - user must enter it
            aadhar_auto_filled: false, // Not auto-filled for new remitters
            ekyc_id: '',
            stateresp: '',
            pid_data: ''
          }));
          
          // Save ekyc_id and stateresp from response ONLY if they come from PaySprint (not localStorage)
          // But still require fresh KYC - these might be from a previous failed attempt
          if (responseData?.ekyc_id && responseData?.stateresp) {
            // Only use if they're from the current response (not from localStorage)
            // For unregistered numbers, always require fresh E-KYC
            console.log('⚠️ Response code 2: Unregistered number detected. Fresh E-KYC required regardless of response data.');
          }
          
          // ALWAYS require fresh E-KYC for unregistered numbers (response_code 2)
          setSuccess('Remitter not registered. Please enter Aadhar number and complete E-KYC.');
          setCurrentStep(2); // Go to E-KYC - user must enter Aadhar and capture fingerprint
        } else if (responseCode === 3) {
          // OTP has been sent - remitter needs to register
          // CRITICAL: Verify that E-KYC was actually done before allowing registration
          // For unregistered numbers, E-KYC must be completed in this session
          
          // Check if E-KYC was done in this session (not from localStorage)
          // We need to verify that pid_data was captured and E-KYC was performed
          const hasValidEkyc = remitterData.ekyc_id && remitterData.stateresp && remitterData.pid_data;
          
          if (!hasValidEkyc) {
            // E-KYC not done - cannot proceed to registration
            // Clear any stale data and require fresh KYC
            const storageKey = `dmt_ekyc_${remitterData.mobile}`;
            localStorage.removeItem(storageKey);
            
            setRemitterData(prev => ({
              ...prev,
              aadhar: '', // Clear Aadhar - user must enter it
              aadhar_auto_filled: false, // Not auto-filled
              ekyc_id: '',
              stateresp: '',
              pid_data: ''
            }));
            
            setError('E-KYC must be completed before registration. Please enter Aadhar number and complete E-KYC first.');
            setCurrentStep(2); // Go to E-KYC - DO NOT SKIP
            return;
          }
          
          // Extract ekyc_id and stateresp from response if available
          if (responseData?.ekyc_id && responseData?.stateresp) {
            setRemitterData(prev => ({
              ...prev,
              ekyc_id: responseData.ekyc_id,
              stateresp: responseData.stateresp
            }));
          }
          
          setSuccess('OTP has been sent to remitter mobile number. Please enter OTP to complete registration.');
          setCurrentStep(3); // Go to Registration - E-KYC verified
        } else if (responseCode === 0) {
          // Unable to get remitter record - treat as unregistered
          // Clear any stale E-KYC data and Aadhar
          const storageKey = `dmt_ekyc_${remitterData.mobile}`;
          localStorage.removeItem(storageKey);
          setRemitterData(prev => ({
            ...prev,
            aadhar: '', // Clear Aadhar - user must enter it
            aadhar_auto_filled: false, // Not auto-filled
            ekyc_id: '',
            stateresp: '',
            pid_data: ''
          }));
          setError('Unable to get remitter record. Please enter Aadhar number and complete E-KYC.');
          setCurrentStep(2); // Go to E-KYC
        } else if (responseCode === 5) {
          setError(responseData?.message || 'Bank response message received. Please check details.');
        } else if (responseCode === 6) {
          setError('Please retry again.');
        } else if (responseCode === 7) {
          setError('This mobile number is not authorized to avail DMT transaction facility.');
        } else {
          // Other response codes or unknown - treat as unregistered
          const message = response.data.data?.message || response.data.message || 'Remitter not found. Please complete registration process.';
          
          // If response_code is missing or 0, check if remitter data exists in response
          // Sometimes PaySprint returns success=true with data even if response_code is not 1
          if (responseData && Object.keys(responseData).length > 0 && responseCode === 1) {
            // If we have data and response_code is 1, might be registered - try fetching beneficiaries
            console.log('⚠️ Unknown response_code but data exists, trying to fetch beneficiaries...');
            setSuccess('Checking remitter status...');
            await fetchBeneficiaries(false);
            setCurrentStep(4); // Go to beneficiary management
          } else {
            // Unregistered or unknown status - require fresh KYC
            // Clear any stale E-KYC data
            const storageKey = `dmt_ekyc_${remitterData.mobile}`;
            localStorage.removeItem(storageKey);
            setRemitterData(prev => ({
              ...prev,
              ekyc_id: '',
              stateresp: '',
              pid_data: ''
            }));
            setSuccess(message);
            setCurrentStep(2); // Go to E-KYC as default - DO NOT SKIP
          }
        }
      } else {
        setError(response.data.message || 'Failed to query remitter');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: E-KYC
  const handleEkyc = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate Aadhar number (mandatory)
    if (!remitterData.aadhar || remitterData.aadhar.length !== 12) {
      setError('Please enter a valid 12-digit Aadhar number.');
      setLoading(false);
      return;
    }

    // Validate fingerprint/PID data (mandatory)
    const pidDataToUse = remitterData.pid_data || pidData;
    if (!pidDataToUse || pidDataToUse.trim() === '') {
      setError('Please capture fingerprint using the "Capture Fingerprint" button. Fingerprint is mandatory for E-KYC.');
      setLoading(false);
      return;
    }

    try {
      // Build payload according to PaySprint E-KYC API requirements
      // Required: mobile, aadhaar_number, data (encrypted PID)
      // Optional: lat, long, accessmode, is_iris
      const ekycPayload = {
        mobile: remitterData.mobile,
        aadhar: remitterData.aadhar,
        aadhaar_number: remitterData.aadhar, // Also send as aadhaar_number for compatibility
        pid_data: pidDataToUse,
        data: pidDataToUse, // Also send as 'data' field for compatibility (encrypted PID data)
        is_iris: remitterData.is_iris || 2, // Default: 2 (Finger)
        accessmode: remitterData.accessmode || 'WEB', // Required for Android app: use 'APP'
        // Optional: lat, long (can be added if needed, but PaySprint has defaults)
      };

      console.log('🔵 E-KYC REQUEST PAYLOAD', ekycPayload);

      const res = await api.post('/api/dmt/remitter/ekyc', ekycPayload);

      console.log('🟢 EKYC API RAW RESPONSE', res.data);

      // 1st layer: Laravel JSON
      const outer = res.data || {};
      // 2nd layer: PaySprint wrapper (status, response_code, message, data)
      const ps = outer.data && !Array.isArray(outer.data) ? outer.data : outer;
      // 3rd layer: actual details (mobile, ekyc_id, stateresp)
      const core = ps.data && !Array.isArray(ps.data) ? ps.data : ps;

      console.log('🟢 PARSED EKYC DATA', core);

      // E-KYC ke baad, jab tum core object nikal chuke ho:
      const { mobile: ekycMobile, ekyc_id, stateresp } = core;

      if (!ekyc_id || !stateresp) {
        console.warn('❌ E-KYC response missing ekyc_id or stateresp', core);
        setError('Unable to read E-KYC details from provider. Please retry.');
        setLoading(false);
        return;
      }

      setRemitterData(prev => ({
        ...prev,
        mobile: ekycMobile || prev.mobile,
        ekyc_id,
        stateresp,
      }));

      console.log('🟢 REMITTER DATA AFTER E-KYC =>', {
        ...remitterData,
        mobile: ekycMobile || remitterData.mobile,
        ekyc_id,
        stateresp,
      });

      // Show success message with E-KYC details
      setSuccess(`✅ E-KYC completed successfully! E-KYC ID: ${ekyc_id}, State Resp: ${stateresp}. Please enter OTP to register.`);
      setCurrentStep(3);
    } catch (err) {
      console.error('🔴 E-KYC ERROR', err);
      setError(
        err?.response?.data?.message ||
        'Unable to complete E-KYC. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Register Remitter
  const handleRegisterRemitter = async () => {
    // Clear old messages
    setError('');
    setSuccess('');

    const { mobile, otp, ekyc_id, stateresp, otp_ref_number } = remitterData;

    // Basic checks
    if (!mobile) {
      setError('Please enter remitter mobile number.');
      return;
    }

    if (!otp) {
      setError('Please enter the OTP sent to remitter mobile.');
      return;
    }

    // 🚫 HARD BLOCK: E-KYC IDs must be present
    if (!ekyc_id || !stateresp) {
      console.warn('❌ Cannot register remitter: missing ekyc_id or stateresp', {
        ekyc_id,
        stateresp,
      });
      setError('Please complete E-KYC first. (ekyc_id / stateresp missing)');
      return; // <<<< YAHAN SE BAHAR NIKAL JAO, API CALL MAT KARO
    }

    const payload = {
      mobile,
      otp,
      ekyc_id,
      stateresp,
    };

    // otp_ref_number optional hai – sirf agar hai to bhejo
    if (otp_ref_number) {
      payload.otp_ref_number = otp_ref_number;
    }

    console.log('🟣 SENDING REGISTER PAYLOAD =>', payload);

    setLoading(true);
    try {
      const res = await api.post('/api/dmt/remitter/register', payload);

      console.log('🟣 REGISTER RESPONSE =>', res.data);

      // Check for success - PaySprint returns response_code: 1 for success
      const responseData = res.data?.data || res.data;
      const responseCode = responseData?.response_code ?? res.data?.response_code;
      const isSuccess = res.data?.success === true || responseCode === 1;

      if (isSuccess) {
        const successMsg = res.data?.message || responseData?.message || 'Remitter registered successfully!';
        setSuccess(`${successMsg} Moving to beneficiary management...`);
        
        // Clear E-KYC data from localStorage since registration is complete
        if (mobile) {
          const storageKey = `dmt_ekyc_${mobile}`;
          localStorage.removeItem(storageKey);
        }
        
        // Automatically move to Step 4 (Beneficiary) after successful registration
        setTimeout(() => {
          setCurrentStep(4);
          fetchBeneficiaries(false); // Silent fetch
        }, 1500);
      } else {
        const errorMsg = res.data?.message || responseData?.message || 'Registration failed. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('❌ REGISTER REMITTER ERROR =>', err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to register remitter. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Add Beneficiary
  const handleAddBeneficiary = async () => {
    if (!beneficiaryData.benename || !beneficiaryData.accno || !beneficiaryData.ifsccode) {
      setError('Please fill all required beneficiary fields');
      return;
    }

    // Convert bank name to numeric bank ID for PaySprint (if bankid not provided)
    const getBankId = (bankName, providedBankId) => {
      // If bankid is explicitly provided and valid, use it
      if (providedBankId && providedBankId !== '0' && providedBankId !== '' && providedBankId !== null) {
        return String(providedBankId).trim();
      }
      // Otherwise, try to map from bank name
      if (bankName && bankName.trim() !== '') {
        const bankMap = {
          'hdfc': '1',
          'sbi': '2', 
          'icici': '3',
          'axis': '4',
          'kotak': '5',
          'pnb': '6',
          'bob': '7',
          'canara': '8',
          'union': '9',
          'indian': '10',
          'state bank': '2',
          'state bank of india': '2',
          'punjab national bank': '6',
          'bank of baroda': '7',
          'union bank': '9',
          'indian bank': '10'
        };
        const bankNameLower = bankName.toLowerCase().trim();
        // First try exact match
        if (bankMap[bankNameLower]) {
          return bankMap[bankNameLower];
        }
        // Then try partial match
        for (const [key, value] of Object.entries(bankMap)) {
          if (bankNameLower.includes(key) || key.includes(bankNameLower)) {
            return value;
          }
        }
      }
      return null; // Return null if no valid mapping found
    };

    const bankid = getBankId(beneficiaryData.bene_bank, beneficiaryData.bankid);
    
    if (!bankid || bankid === '0') {
      setError('Bank ID is required. Please enter a valid bank name (e.g., HDFC, SBI, ICICI) or enter Bank ID directly (1-10).');
      return;
    }

    // Optimistically add beneficiary locally so that UI always shows it
    const tempId = Date.now();
    const optimisticBeneficiary = {
      bene_id: tempId,
      bene_name: beneficiaryData.benename,
      bene_account: beneficiaryData.accno,
      bene_ifsc: beneficiaryData.ifsccode,
      bene_bank: beneficiaryData.bene_bank,
      verified: beneficiaryData.verified || '0',
      bankid
    };

    setBeneficiaries(prev => [...prev, optimisticBeneficiary]);

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt/beneficiary/register', {
        mobile: remitterData.mobile,
        benename: beneficiaryData.benename,
        bankid: bankid,
        accno: beneficiaryData.accno,
        ifsccode: beneficiaryData.ifsccode,
        verified: beneficiaryData.verified || '0'
      });

      if (response.data.success) {
        setSuccess('Beneficiary added successfully!');

        // If API returned real bene_id / details, update the optimistic row
        const wrapper = response.data?.data || {};
        const inner = wrapper?.data || wrapper;

        if (inner) {
          setBeneficiaries(prev => {
            const updated = prev.map(b =>
              b.bene_id === tempId
                ? {
                    ...b,
                    bene_id: inner.bene_id || inner.beneid || inner.id || b.bene_id,
                    bene_name: inner.bene_name || inner.name || inner.benename || b.bene_name,
                    bene_account: inner.bene_account || inner.accno || inner.account_number || b.bene_account,
                    bene_ifsc: inner.bene_ifsc || inner.ifsccode || inner.ifsc || b.bene_ifsc,
                    bene_bank: inner.bene_bank || inner.bankname || inner.bank_name || b.bene_bank,
                    verified: inner.verified || b.verified,
                    bankid: inner.bankid || inner.bank_id || b.bankid
                  }
                : b
            );
            
            // Store in localStorage for persistence across logins
            if (remitterData.mobile && remitterData.mobile.length === 10) {
              const storageKey = `dmt_beneficiaries_${remitterData.mobile}`;
              localStorage.setItem(storageKey, JSON.stringify(updated));
            }
            
            return updated;
          });
        }

        setBeneficiaryData({
          benename: '',
          accno: '',
          ifsccode: '',
          bankid: '',
          bene_bank: '',
          verified: '0'
        });

        setTimeout(() => {
          setSuccess('Beneficiary added! You can now proceed to make a transaction.');
        }, 1000);
      } else {
        // Remove optimistic row on failure
        setBeneficiaries(prev => prev.filter(b => b.bene_id !== tempId));
        setError(response.data.message || 'Failed to add beneficiary');
      }
    } catch (err) {
      // Remove optimistic row on error
      setBeneficiaries(prev => prev.filter(b => b.bene_id !== tempId));
      setError(err.response?.data?.message || 'Failed to add beneficiary');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Beneficiaries
  const fetchBeneficiaries = async (showMessage = false) => {
    try {
      const response = await api.post('/api/dmt/beneficiary/fetch', {
        mobile: remitterData.mobile
      });

      // Try to extract beneficiaries even if success is false (PaySprint might return partial data)
      const paySprintData = response.data.data;
      
      // Extract beneficiaries array from PaySprint response
      let beneficiariesList = [];
      
      if (paySprintData?.data && Array.isArray(paySprintData.data)) {
        // PaySprint format: { status: true, data: [{ bene_id, name, accno, ifsccode, ... }] }
        beneficiariesList = paySprintData.data;
      } else if (paySprintData?.beneficiaries && Array.isArray(paySprintData.beneficiaries)) {
        // Alternative format: { beneficiaries: [...] }
        beneficiariesList = paySprintData.beneficiaries;
      } else if (Array.isArray(paySprintData)) {
        // Direct array format
        beneficiariesList = paySprintData;
      }
      
      if (beneficiariesList.length > 0) {
        // Normalize field names (PaySprint might use 'name' instead of 'bene_name', etc.)
        const normalizedBeneficiaries = beneficiariesList.map(bene => ({
          bene_id: bene.bene_id || bene.beneid || bene.id,
          bene_name: bene.bene_name || bene.name || bene.benename,
          bene_account: bene.bene_account || bene.accno || bene.account_number,
          bene_ifsc: bene.bene_ifsc || bene.ifsccode || bene.ifsc,
          bene_bank: bene.bene_bank || bene.bankname || bene.bank_name,
          verified: bene.verified || '0',
          bankid: bene.bankid || bene.bank_id
        }));
        
        setBeneficiaries(normalizedBeneficiaries);
        
        // Store beneficiaries in localStorage for persistence across logins
        if (remitterData.mobile && remitterData.mobile.length === 10) {
          const storageKey = `dmt_beneficiaries_${remitterData.mobile}`;
          localStorage.setItem(storageKey, JSON.stringify(normalizedBeneficiaries));
        }
        
        if (showMessage) {
          setSuccess(`Loaded ${normalizedBeneficiaries.length} beneficiary(ies) from PaySprint.`);
        }
        } else {
          // No beneficiaries found or API returned empty
      if (response.data.success) {
            // API succeeded but no beneficiaries
            setBeneficiaries([]);
            // Clear from localStorage if API confirms no beneficiaries
            if (remitterData.mobile && remitterData.mobile.length === 10) {
              const storageKey = `dmt_beneficiaries_${remitterData.mobile}`;
              localStorage.removeItem(storageKey);
            }
            if (showMessage) {
              setSuccess('No beneficiaries found. You can add a new beneficiary above.');
            }
          } else {
          // API failed (likely PaySprint 500 error)
          // Don't clear existing beneficiaries - they might exist but API is down
          if (showMessage) {
            setError('Unable to fetch beneficiaries from PaySprint (API error). Your previously added beneficiaries may still exist. You can add a new beneficiary or try refreshing.');
          } else {
            // Silent failure - just log it
            console.warn('PaySprint fetch beneficiaries failed:', response.data.message);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch beneficiaries:', err);
      // Don't clear existing beneficiaries on error
      if (showMessage) {
        setError('Failed to fetch beneficiaries. Your previously added beneficiaries may still exist. You can add a new beneficiary or try refreshing.');
      }
    }
  };

  // Fetch Beneficiary by ID
  const fetchBeneficiaryById = async (beneId) => {
    try {
      const response = await api.post('/api/dmt/beneficiary/fetch-by-id', {
        mobile: remitterData.mobile,
        beneid: beneId
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Failed to fetch beneficiary details');
        return null;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch beneficiary details');
      return null;
    }
  };

  // Delete Beneficiary
  const handleDeleteBeneficiary = async (beneId) => {
    if (!window.confirm('Are you sure you want to delete this beneficiary?')) return;

    setLoading(true);
    try {
      const response = await api.post('/api/dmt/beneficiary/delete', {
        mobile: remitterData.mobile,
        bene_id: beneId
      });

      if (response.data.success) {
        setSuccess('Beneficiary deleted successfully!');
        // Remove from localStorage
        if (remitterData.mobile && remitterData.mobile.length === 10) {
          const storageKey = `dmt_beneficiaries_${remitterData.mobile}`;
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            try {
              const storedBeneficiaries = JSON.parse(stored);
              const updated = storedBeneficiaries.filter(b => b.bene_id !== beneId);
              localStorage.setItem(storageKey, JSON.stringify(updated));
            } catch (e) {
              console.error('Failed to update localStorage:', e);
            }
          }
        }
        await fetchBeneficiaries(false); // Silent fetch
      } else {
        setError(response.data.message || 'Failed to delete beneficiary');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete beneficiary');
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Penny Drop Verification
  const handlePennyDrop = async () => {
    if (!selectedBeneficiary) {
      setError('Please select a beneficiary first');
      return;
    }

    // Validate required beneficiary fields
    if (!selectedBeneficiary.bene_account && !selectedBeneficiary.accno && !selectedBeneficiary.account_number) {
      setError('Beneficiary account number is missing. Please select a valid beneficiary.');
      return;
    }

    if (!selectedBeneficiary.bankid && !selectedBeneficiary.bank_id) {
      setError('Beneficiary bank ID is missing. Please select a valid beneficiary.');
      return;
    }

    if (!selectedBeneficiary.bene_name && !selectedBeneficiary.name && !selectedBeneficiary.benename) {
      setError('Beneficiary name is missing. Please select a valid beneficiary.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Generate unique numeric referenceid (PaySprint expects integer for penny drop)
      // Use timestamp + random suffix, but ensure it's a valid integer
      // PaySprint accepts large integers, so we combine timestamp with random
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 1000); // 0-999 for uniqueness
      // Combine: timestamp (13 digits) + random (3 digits) = 16 digits max
      const referenceId = `${timestamp}${String(randomSuffix).padStart(3, '0')}`;

      // Extract beneficiary details with fallbacks for different field names
      const accno = selectedBeneficiary.bene_account || selectedBeneficiary.accno || selectedBeneficiary.account_number;
      const bankid = selectedBeneficiary.bankid || selectedBeneficiary.bank_id;
      const benename = selectedBeneficiary.bene_name || selectedBeneficiary.name || selectedBeneficiary.benename;
      const bene_id = selectedBeneficiary.bene_id || selectedBeneficiary.beneid || selectedBeneficiary.id;

      const response = await api.post('/api/dmt/transaction/penny-drop', {
        mobile: remitterData.mobile,
        bene_id: bene_id,
        accno: accno,
        bankid: bankid,
        benename: benename,
        referenceid: referenceId // Note: PaySprint expects "referenceid" not "reference_id"
      });

      if (response.data.success) {
        // Don't store penny drop reference ID - PaySprint requires a NEW unique reference ID for send-otp
        // We'll generate a fresh one when sending OTP
        // Just mark that penny drop is complete
        setTransactionData(prev => ({ ...prev, penny_drop_complete: true }));
        
        setSuccess('Penny drop successful! Account verified. You can now proceed with transaction.');
        setCurrentStep(6); // Go to transaction OTP
      } else {
        // Handle duplicate reference ID error specifically
        const errorMessage = response.data.message || response.data.data?.message || 'Penny drop failed. Please verify beneficiary details.';
        if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('reference')) {
          setError('Duplicate reference ID detected. Please try again - a new reference ID will be generated.');
          // Don't block the user - they can retry
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors || 'Penny drop failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Send Transaction OTP
  const handleSendTransactionOTP = async () => {
    if (!selectedBeneficiary || !transactionData.amount) {
      setError('Please select a beneficiary and enter amount');
      return;
    }

    // Extract bene_id (may be stored as bene_id, beneid, or id)
    // PaySprint returns bene_id as a number, but backend validation requires it as a string
    const bene_id_raw = selectedBeneficiary.bene_id || selectedBeneficiary.beneid || selectedBeneficiary.id;
    if (!bene_id_raw) {
      setError('Beneficiary ID is missing. Please select a beneficiary again.');
      return;
    }
    // Convert to string explicitly (backend validation requires string type)
    const bene_id = String(bene_id_raw);

    const amount = parseFloat(transactionData.amount);
    if (isNaN(amount) || amount < 1 || amount > 500000) {
      setError('Amount must be between ₹1 and ₹5,00,000');
      return;
    }

    // Validate mobile number
    if (!remitterData.mobile || remitterData.mobile.length !== 10) {
      setError('Remitter mobile number is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Generate a NEW unique reference ID for send-otp
      // PaySprint requires a different reference ID than the one used in penny drop
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 10000); // Increased range for better uniqueness
      const referenceId = `${timestamp}${String(randomSuffix).padStart(4, '0')}`;
      setTransactionData(prev => ({ ...prev, reference_id: referenceId }));

      // Prepare request payload
      // IMPORTANT: All string fields must be explicitly strings (not numbers)
      // Backend validation is strict about data types
      const requestPayload = {
        mobile: String(remitterData.mobile), // Ensure it's a string
        amount: amount, // Number (backend expects numeric)
        bene_id: String(bene_id), // CRITICAL: Must be string, not number
        referenceid: String(referenceId), // Ensure it's a string
        txntype: 'IMPS', // Transaction type: 'IMPS' or 'NEFT'
        // Optional fields - backend will use defaults if empty
        pincode: remitterData.pincode ? String(remitterData.pincode) : '',
        address: remitterData.address ? String(remitterData.address) : '',
        dob: '',
        gst_state: ''
      };

      // Log request for debugging (verify types)
      console.log('🔵 Send OTP Request Payload:', {
        ...requestPayload,
        bene_id_type: typeof requestPayload.bene_id,
        bene_id_value: requestPayload.bene_id,
        mobile_type: typeof requestPayload.mobile
      });

      const response = await api.post('/api/dmt/transaction/send-otp', requestPayload);

      if (response.data.success) {
        // Extract stateresp from response (required for process transaction)
        // PaySprint response structure: {status: true, response_code: 1, message: "...", stateresp: "838323002"}
        // Laravel wraps it in: {success: true, message: "...", data: {status: true, ..., stateresp: "838323002"}}
        const paySprintResponse = response.data.data || {};
        const stateresp = paySprintResponse.stateresp || response.data.stateresp || '';
        
        console.log('🔵 Send OTP Response Structure:', {
          fullResponse: response.data,
          paySprintResponse: paySprintResponse,
          extractedStateresp: stateresp
        });
        
        // Store stateresp in transactionData for process transaction
        setTransactionData(prev => ({ ...prev, stateresp }));
        
        // Show success message with UAT guidance
        let successMsg = 'OTP sent successfully! Please enter OTP to complete transaction.';
        if (stateresp) {
          successMsg += ` (UAT: If OTP not received via SMS, use test OTP: 9999 or stateresp: ${stateresp})`;
        } else {
          successMsg += ' (UAT: If OTP not received via SMS, use test OTP: 9999)';
        }
        setSuccess(successMsg);
        setCurrentStep(7); // Go to transaction confirmation
      } else {
        // Show detailed validation errors if available
        const errorMsg = response.data.message || 'Failed to send OTP';
        const errors = response.data.errors;
        if (errors && Object.keys(errors).length > 0) {
          const errorDetails = Object.entries(errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          setError(`${errorMsg} - ${errorDetails}`);
        } else {
          setError(errorMsg);
        }
      }
    } catch (err) {
      // Show detailed validation errors from backend
      const errorMsg = err.response?.data?.message || 'Failed to send OTP';
      const errors = err.response?.data?.errors;
      if (errors && Object.keys(errors).length > 0) {
        const errorDetails = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        setError(`${errorMsg} - ${errorDetails}`);
      } else {
        setError(errorMsg);
      }
      console.error('Send OTP Error:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Process Transaction
  const handleProcessTransaction = async () => {
    if (!transactionData.otp || transactionData.otp.length < 4) {
      setError('Please enter a valid OTP (minimum 4 digits, UAT test OTP: 9999)');
      return;
    }

    // Check if stateresp is available
    const stateresp = transactionData.stateresp || remitterData.stateresp || '';
    if (!stateresp) {
      console.warn('⚠️ stateresp not found in transactionData or remitterData');
      setError('Transaction state response (stateresp) is missing. Please try sending OTP again.');
      return;
    }

    console.log('🔵 Process Transaction Payload:', {
      mobile: remitterData.mobile,
      amount: transactionData.amount,
      bene_id: selectedBeneficiary.bene_id,
      otp: transactionData.otp,
      reference_id: transactionData.reference_id,
      stateresp: stateresp
    });

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt/transaction/process', {
        mobile: remitterData.mobile,
        amount: parseFloat(transactionData.amount),
        bene_id: selectedBeneficiary.bene_id,
        otp: transactionData.otp,
        reference_id: transactionData.reference_id,
        stateresp: stateresp, // Required for PaySprint
        // Optional fields (will use backend defaults if not provided)
        pincode: remitterData.pincode || '',
        address: remitterData.address || '',
        dob: remitterData.dob || '',
        gst_state: remitterData.gst_state || '',
        txntype: transactionData.txntype || 'IMPS'
      });

      if (response.data.success) {
        const txnStatus = response.data.data?.status || 'processing';
        let statusMessage = '';
        
        switch (txnStatus) {
          case 'success':
            statusMessage = `Transaction successful! Money credited to beneficiary account. Transaction ID: ${response.data.data?.transaction_id || 'N/A'}`;
            break;
          case 'failed':
            statusMessage = `Transaction failed. ${response.data.message || 'Please try again or contact support.'}`;
            // Add refund option for failed transactions
            setTimeout(() => {
              if (window.confirm('Transaction failed. Would you like to process a refund?')) {
                handleSendRefundOTP(transactionData.reference_id);
              }
            }, 2000);
            break;
          case 'hold':
            statusMessage = `Transaction is on hold. This may be due to bank node issues. Please check status later. Transaction ID: ${response.data.data?.transaction_id || 'N/A'}`;
            break;
          case 'processing':
            statusMessage = `Transaction is processing. Please check status in a few minutes. Transaction ID: ${response.data.data?.transaction_id || 'N/A'}`;
            break;
          default:
            statusMessage = `Transaction status: ${txnStatus}. Transaction ID: ${response.data.data?.transaction_id || 'N/A'}`;
        }
        
        if (txnStatus === 'success') {
          setSuccess(statusMessage);
          onSuccess?.(response.data.data);
        } else {
          setError(statusMessage);
        }
        setCurrentStep(8); // Show final status
      } else {
        setError(response.data.message || 'Transaction failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  // Send Refund OTP
  const handleSendRefundOTP = async (referenceId) => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt/refund/send-otp', {
        reference_id: referenceId
      });

      if (response.data.success) {
        setSuccess('Refund OTP sent successfully! Please enter OTP to process refund.');
        setRefundData(prev => ({ ...prev, reference_id: referenceId, otp: '' }));
        setCurrentStep(9); // Go to refund confirmation
      } else {
        setError(response.data.message || 'Failed to send refund OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send refund OTP');
    } finally {
      setLoading(false);
    }
  };

  // Process Refund
  const handleProcessRefund = async () => {
    if (!refundData.otp || refundData.otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt/refund/claim', {
        reference_id: refundData.reference_id,
        otp: refundData.otp
      });

      if (response.data.success) {
        setSuccess('Refund processed successfully! Amount has been credited back to your wallet.');
        setCurrentStep(10); // Show refund success
      } else {
        setError(response.data.message || 'Refund processing failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Refund processing failed');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setCurrentStep(1);
    setRemitterData({
      mobile: '',
      name: '',
      aadhar: '',
      aadhar_auto_filled: false, // Reset auto-fill flag
      pincode: '',
      address: '',
      is_iris: 2,
      accessmode: 'WEB',
      otp: '',
      stateresp: '',
      ekyc_id: '',
      otp_ref_number: '',
      pid_data: ''
    });
    setBeneficiaryData({
      bene_name: '',
      bene_account: '',
      bene_ifsc: '',
      bene_bank: '',
      bene_mobile: ''
    });
    setTransactionData({
      amount: '',
      bene_id: '',
      otp: '',
      stateresp: '',
      reference_id: ''
    });
    setRefundData({
      reference_id: '',
      otp: ''
    });
    setFingerprintStatus('idle');
    setPidData('');
    setError('');
    setSuccess('');
    setBeneficiaries([]);
    setSelectedBeneficiary(null);
    
    // Clear E-KYC data from localStorage
    if (remitterData.mobile) {
      const ekycStorageKey = `dmt_ekyc_${remitterData.mobile}`;
      localStorage.removeItem(ekycStorageKey);
      
      // Clear beneficiaries from localStorage
      const beneficiariesStorageKey = `dmt_beneficiaries_${remitterData.mobile}`;
      localStorage.removeItem(beneficiariesStorageKey);
    }
  };

  const handleClose = () => {
    handleReset();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaMoneyBillWave className="me-2" />
          Comprehensive DMT Service
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Progress Steps */}
        <div className="mb-4">
          <div className="d-flex justify-content-between">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className={`text-center ${currentStep >= step ? 'text-primary' : 'text-muted'}`}>
                <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= step ? 'bg-primary text-white' : 'bg-light'}`} style={{ width: '30px', height: '30px' }}>
                  {step}
                </div>
                <div className="small mt-1">
                  {step === 1 && 'Query'}
                  {step === 2 && 'E-KYC'}
                  {step === 3 && 'Register'}
                  {step === 4 && 'Beneficiary'}
                  {step === 5 && 'Transaction'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Step 1: Query Remitter */}
        {currentStep === 1 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 1: Query Remitter</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Number *</Form.Label>
                <Form.Control
                  type="tel"
                  value={remitterData.mobile}
                  onChange={(e) => setRemitterData(prev => ({ ...prev, mobile: e.target.value }))}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                />
              </Form.Group>
              <Button onClick={handleQueryRemitter} disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Query Remitter
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 2: E-KYC */}
        {currentStep === 2 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 2: E-KYC Registration</h5>
            </Card.Header>
            <Card.Body>
              {/* Show different messages based on remitter status */}
              {remitterData.aadhar_auto_filled ? (
                <Alert variant="success" className="mb-3">
                  <strong>✅ Registered Remitter Detected!</strong><br/>
                  Aadhar number has been auto-filled from your registered account. Please capture fingerprint to continue.
                </Alert>
              ) : (
                <Alert variant="warning" className="mb-3">
                  <strong>⚠️ New Remitter</strong><br/>
                  Please enter your Aadhar number and capture fingerprint to complete E-KYC registration.
                </Alert>
              )}
              
              {/* Check if E-KYC already completed (for skip option) */}
              {remitterData.ekyc_id && remitterData.stateresp && !remitterData.aadhar_auto_filled && (
                <Alert variant="info" className="mb-3">
                  <strong>ℹ️ E-KYC Already Completed!</strong><br/>
                  E-KYC ID: <strong>{remitterData.ekyc_id}</strong> | State Resp: <strong>{remitterData.stateresp}</strong><br/>
                  <small>You can skip E-KYC and proceed directly to registration. Click "Skip to Registration" below.</small>
                </Alert>
              )}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Aadhar Number *
                      {remitterData.aadhar_auto_filled && (
                        <span className="text-success ms-2">
                          <FaCheckCircle /> Auto-filled from registered remitter
                        </span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={remitterData.aadhar}
                      onChange={(e) => setRemitterData(prev => ({ ...prev, aadhar: e.target.value }))}
                      placeholder={remitterData.aadhar_auto_filled ? "Aadhar number (auto-filled)" : "Enter 12-digit Aadhar"}
                      maxLength={12}
                      readOnly={remitterData.aadhar_auto_filled} // Read-only when auto-filled
                      className={remitterData.aadhar_auto_filled ? 'bg-light' : ''} // Visual indicator
                    />
                    <Form.Text className="text-muted">
                      {remitterData.aadhar_auto_filled 
                        ? 'Aadhar number auto-filled from registered remitter. Please capture fingerprint to continue.'
                        : 'Enter the remitter\'s 12-digit Aadhaar number'}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>KYC Mode</Form.Label>
                    <Form.Select
                      value={remitterData.is_iris}
                      onChange={(e) => setRemitterData(prev => ({ ...prev, is_iris: parseInt(e.target.value) }))}
                    >
                      <option value={2}>Finger Print (Default)</option>
                      <option value={1}>IRIS</option>
                      <option value={4}>Face Auth</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Select biometric modality for E-KYC
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
              <Form.Group className="mb-3">
                    <Form.Label>Access Mode</Form.Label>
                    <Form.Select
                      value={remitterData.accessmode}
                      onChange={(e) => setRemitterData(prev => ({ ...prev, accessmode: e.target.value }))}
                    >
                      <option value="WEB">WEB (Default)</option>
                      <option value="APP">APP (Required for Android application)</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Required for Android application to get E-KYC success
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Fingerprint Capture Section */}
              <Form.Group className="mb-3">
                <Form.Label>
                  PID Data (Fingerprint) {fingerprintStatus === 'verified' && (
                    <span className="text-success ms-2">
                      <FaCheckCircle /> Captured
                    </span>
                  )}
                </Form.Label>
                <div className="d-flex gap-2">
                <Form.Control
                  as="textarea"
                    rows={2}
                    placeholder={fingerprintStatus === 'verified' ? 'Fingerprint captured successfully ✓' : 'Click "Capture Fingerprint" to capture from biometric device, or use test data for UAT'}
                    value={remitterData.pid_data || pidData || ''}
                    onChange={(e) => {
                      setRemitterData(prev => ({ ...prev, pid_data: e.target.value }));
                      setPidData(e.target.value);
                    }}
                    readOnly={fingerprintStatus === 'capturing'}
                    className={fingerprintStatus === 'verified' ? 'border-success' : ''}
                  />
                  <Button
                    variant={fingerprintStatus === 'verified' ? 'success' : fingerprintStatus === 'capturing' ? 'warning' : 'primary'}
                    onClick={captureFingerprint}
                    disabled={loading || fingerprintStatus === 'capturing'}
                    style={{ minWidth: '180px' }}
                  >
                    {fingerprintStatus === 'capturing' ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Capturing...
                      </>
                    ) : fingerprintStatus === 'verified' ? (
                      <>
                        <FaCheckCircle className="me-2" />
                        Re-capture
                      </>
                    ) : (
                      <>
                        <FaFingerprint className="me-2" />
                        Capture Fingerprint
                      </>
                    )}
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  {fingerprintStatus === 'idle' && 'Click "Capture Fingerprint" to capture from RD device (FM220U), or leave empty to use test data for UAT.'}
                  {fingerprintStatus === 'capturing' && 'Capturing fingerprint... Please place your finger on the device.'}
                  {fingerprintStatus === 'verified' && 'Fingerprint captured and verified successfully. PID Data ready for E-KYC.'}
                  {fingerprintStatus === 'failed' && 'Fingerprint capture failed. Please try again or use test data for UAT.'}
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button onClick={handleEkyc} disabled={loading || fingerprintStatus === 'capturing'}>
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Complete E-KYC
                </Button>
                {/* Hide "Skip to Registration" for registered remitters - they must capture fingerprint */}
                {!remitterData.aadhar_auto_filled && (
                  <Button 
                    variant={remitterData.ekyc_id && remitterData.stateresp ? "success" : "outline-primary"}
                    onClick={() => {
                      if (remitterData.ekyc_id && remitterData.stateresp) {
                        setSuccess('E-KYC already completed! Proceeding to registration...');
                      } else {
                        setSuccess('Skipped E-KYC. Please register with OTP.');
                      }
                      setCurrentStep(3);
                    }}
                    disabled={loading || fingerprintStatus === 'capturing'}
                  >
                    {remitterData.ekyc_id && remitterData.stateresp ? (
                      <>✓ Skip to Registration (E-KYC Done)</>
                    ) : (
                      <>Skip to Registration</>
                    )}
                  </Button>
                )}
              </div>
              <div className="mt-3">
                <Alert variant="warning" className="mb-0">
                  <small>
                    <strong>⚠️ Important Notes:</strong><br/>
                    • E-KYC requires a biometric device. If you don't have one, you can skip to registration with OTP.<br/>
                    • <strong>Maximum 30 minutes</strong> allowed between E-KYC and remitter registration. After 30 min, E-KYC must be done again.<br/>
                    • <strong>Only 2 attempts allowed per remitter per day.</strong> After 2 failures, remitter cannot do E-KYC for next 24 hours.<br/>
                    • <strong>Remitter name must match Aadhaar card holder name</strong>, otherwise E-KYC will fail and transactions will not be possible.
                </small>
                </Alert>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Step 3: Register Remitter */}
        {currentStep === 3 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 3: Register Remitter</h5>
            </Card.Header>
            <Card.Body>
              {/* E-KYC Success Info */}
              {(remitterData.stateresp || remitterData.ekyc_id) && (
                <Alert variant="success" className="mb-3">
                  <strong>✅ E-KYC Completed Successfully!</strong>
                  {remitterData.ekyc_id && <><br/><strong>E-KYC ID:</strong> {remitterData.ekyc_id}</>}
                  {remitterData.stateresp && <><br/><strong>State Response:</strong> {remitterData.stateresp}</>}
                  <br/><small className="text-muted">Please enter the OTP sent to your mobile number to complete registration.</small>
                </Alert>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Enter OTP *</Form.Label>
                <Form.Control
                  type="text"
                  value={remitterData.otp}
                  onChange={(e) => setRemitterData(prev => ({ ...prev, otp: e.target.value }))}
                  placeholder="Enter OTP (e.g., 9999)"
                  maxLength={6}
                />
                <Form.Text className="text-muted">
                  For UAT testing, use OTP: 9999<br/>
                  <strong>Note:</strong> OTP expires in 90 seconds. You must register within 30 minutes of completing E-KYC.
                </Form.Text>
              </Form.Group>
              <div className="d-flex gap-2">
              <Button onClick={handleRegisterRemitter} disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Register Remitter
              </Button>
                {remitterData.ekyc_id && remitterData.stateresp && (
                  <Button 
                    variant="outline-success" 
                    onClick={() => {
                      setCurrentStep(4);
                      fetchBeneficiaries(false); // Silent fetch
                    }}
                    disabled={loading}
                  >
                    Skip to Beneficiary
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Step 4: Beneficiary Management */}
        {currentStep === 4 && (
          <div>
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Step 4: Add Beneficiary</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Beneficiary Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.benename}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, benename: e.target.value }))}
                        placeholder="Enter beneficiary name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.accno}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, accno: e.target.value }))}
                        placeholder="Enter account number"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IFSC Code *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.ifsccode}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, ifsccode: e.target.value.toUpperCase() }))}
                        placeholder="e.g., SBIN0001234"
                        maxLength={11}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_bank}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_bank: e.target.value }))}
                        placeholder="Enter bank name (e.g., HDFC, SBI, ICICI)"
                      />
                      <Form.Text className="text-muted">
                        Common banks: HDFC(1), SBI(2), ICICI(3), Axis(4), Kotak(5), PNB(6), BOB(7), Canara(8), Union(9), Indian(10)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank ID (Optional)</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bankid}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bankid: e.target.value }))}
                        placeholder="Enter bank ID (1-10) or leave empty for auto-mapping"
                      />
                      <Form.Text className="text-muted">
                        If you know the bank ID, enter it here. Otherwise, it will be auto-mapped from bank name.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Button onClick={handleAddBeneficiary} disabled={loading}>
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  <FaPlus className="me-2" />
                  Add Beneficiary
                </Button>
              </Card.Body>
            </Card>

            {/* Beneficiaries List */}
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Your Beneficiaries</h5>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => fetchBeneficiaries(true)}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    🔄 Refresh
                  </Button>
                  {beneficiaries.length > 0 && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => {
                        if (beneficiaries.length > 0) {
                          setSelectedBeneficiary(beneficiaries[0]);
                          setCurrentStep(5); // Go to Penny Drop / Transaction
                        }
                      }}
                    >
                      Proceed to Transaction →
                    </Button>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                {beneficiaries.length === 0 ? (
                  <div>
                    <p className="text-muted mb-2">No beneficiaries loaded. This could be because:</p>
                    <ul className="text-muted small mb-3">
                      <li>You haven't added any beneficiaries yet</li>
                      <li>PaySprint API is temporarily unavailable (try clicking "Refresh" above)</li>
                      <li>Beneficiaries were added in a previous session but couldn't be fetched</li>
                    </ul>
                    <p className="text-muted">You can add a new beneficiary above, or click "Refresh" to retry fetching existing beneficiaries.</p>
                  </div>
                ) : (
                  <ListGroup>
                    {beneficiaries.map((beneficiary, index) => (
                      <ListGroup.Item key={beneficiary.bene_id || index} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{beneficiary.bene_name || beneficiary.name || 'N/A'}</strong>
                          <br />
                          <small className="text-muted">
                            Account: {beneficiary.bene_account || beneficiary.accno || beneficiary.account_number || 'N/A'} | 
                            IFSC: {beneficiary.bene_ifsc || beneficiary.ifsccode || beneficiary.ifsc || 'N/A'}
                          </small>
                        </div>
                        <div>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={async () => {
                              const details = await fetchBeneficiaryById(beneficiary.bene_id);
                              if (details) {
                                alert(`Beneficiary Details:\nName: ${details.bene_name || details.name}\nAccount: ${details.bene_account || details.accno}\nIFSC: ${details.bene_ifsc || details.ifsccode}\nBank: ${details.bene_bank || details.bankname || 'N/A'}`);
                              }
                            }}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              setSelectedBeneficiary(beneficiary);
                              setCurrentStep(5); // Go to Penny Drop / Transaction
                            }}
                          >
                            Select & Transact
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteBeneficiary(beneficiary.bene_id)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card.Body>
            </Card>
          </div>
        )}

        {/* Step 5: Penny Drop Verification */}
        {currentStep === 5 && selectedBeneficiary && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 5: Verify Beneficiary Account</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Selected Beneficiary:</strong><br />
                {selectedBeneficiary.bene_name} | {selectedBeneficiary.bene_account} | {selectedBeneficiary.bene_ifsc}
              </div>

              <div className="alert alert-info">
                <strong>Penny Drop Verification:</strong><br />
                This will verify the beneficiary account by crediting ₹1 to their account. 
                This helps ensure the account details are correct before making the actual transaction.
              </div>

              <Button onClick={handlePennyDrop} disabled={loading} className="w-100">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Verify Account (Penny Drop)
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 6: Transaction */}
        {currentStep === 6 && selectedBeneficiary && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 6: Make Transaction</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Selected Beneficiary:</strong><br />
                {selectedBeneficiary.bene_name} | {selectedBeneficiary.bene_account} | {selectedBeneficiary.bene_ifsc}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Amount (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  value={transactionData.amount}
                  onChange={(e) => setTransactionData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="Enter amount (₹1 - ₹5,00,000)"
                  min="1"
                  max="500000"
                />
              </Form.Group>

              <div className="mb-3">
                <small className="text-muted mb-2 d-block">Quick Amount Selection:</small>
                <div className="d-flex flex-wrap gap-2">
                  {quickAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setTransactionData(prev => ({ ...prev, amount: amount.toString() }))}
                      className={transactionData.amount === amount.toString() ? 'active' : ''}
                    >
                      ₹{amount}
                    </Button>
                  ))}
                </div>
              </div>

              {transactionData.amount && (
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="d-flex justify-content-between">
                    <span>Transfer Amount:</span>
                    <strong>₹{transactionData.amount}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Service Charge:</span>
                    <strong>₹{Math.max(5, Math.ceil(parseFloat(transactionData.amount) * 0.005))}</strong>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between">
                    <strong>Total Deduction:</strong>
                    <strong className="text-primary">
                      ₹{parseFloat(transactionData.amount) + Math.max(5, Math.ceil(parseFloat(transactionData.amount) * 0.005))}
                    </strong>
                  </div>
                </div>
              )}

              <Button onClick={handleSendTransactionOTP} disabled={loading || !transactionData.amount}>
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Send OTP & Proceed
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 7: Transaction Confirmation */}
        {currentStep === 7 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 7: Confirm Transaction</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Transaction Details:</strong><br />
                <strong>Amount:</strong> ₹{transactionData.amount}<br />
                <strong>Beneficiary:</strong> {selectedBeneficiary?.bene_name}<br />
                <strong>Account:</strong> {selectedBeneficiary?.bene_account}<br />
                <strong>Reference ID:</strong> {transactionData.reference_id}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Enter OTP *</Form.Label>
                <Form.Control
                  type="text"
                  value={transactionData.otp}
                  onChange={(e) => setTransactionData(prev => ({ ...prev, otp: e.target.value }))}
                  placeholder="Enter OTP (UAT: 9999 or 999999)"
                  maxLength={6}
                />
                <Form.Text className="text-muted">
                  OTP sent to your mobile number. Valid for 90 seconds.
                  <br/>
                  <strong>UAT Testing:</strong> If OTP not received via SMS, try test OTP: <strong>9999</strong> (4 digits) or <strong>999999</strong> (6 digits)
                  {transactionData.stateresp && (
                    <span><br/>State Response: <strong>{transactionData.stateresp}</strong> (stored for transaction processing)</span>
                  )}
                </Form.Text>
              </Form.Group>

              <Button onClick={handleProcessTransaction} disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Complete Transaction
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 9: Refund Confirmation */}
        {currentStep === 9 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 9: Process Refund</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Refund Details:</strong><br />
                <strong>Reference ID:</strong> {refundData.reference_id}<br />
                <strong>Status:</strong> Failed Transaction - Refund Available
              </div>

              <div className="alert alert-info">
                <strong>Refund Information:</strong><br />
                This transaction failed and is eligible for refund. The amount will be credited back to your wallet along with all charges.
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Enter Refund OTP *</Form.Label>
                <Form.Control
                  type="text"
                  value={refundData.otp}
                  onChange={(e) => setRefundData(prev => ({ ...prev, otp: e.target.value }))}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                />
                <Form.Text className="text-muted">
                  OTP sent to your mobile number for refund processing.
                </Form.Text>
              </Form.Group>

              <Button onClick={handleProcessRefund} disabled={loading} className="w-100">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Process Refund
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 10: Refund Success */}
        {currentStep === 10 && (
          <Card>
            <Card.Body className="text-center">
              <FaCheckCircle className="text-success mb-3" size={48} />
              <h5 className="text-success">Refund Processed!</h5>
              <p className="text-muted">{success}</p>
              <Button onClick={handleReset} variant="primary">
                Make Another Transaction
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 8: Final Status */}
        {currentStep === 8 && (
          <Card>
            <Card.Body className="text-center">
              {success ? (
                <>
                  <FaCheckCircle className="text-success mb-3" size={48} />
                  <h5 className="text-success">Transaction Completed!</h5>
                  <p className="text-muted">{success}</p>
                </>
              ) : (
                <>
                  <FaExclamationTriangle className="text-warning mb-3" size={48} />
                  <h5 className="text-warning">Transaction Status</h5>
                  <p className="text-muted">{error}</p>
                </>
              )}
              <Button onClick={handleReset} variant="primary">
                Make Another Transaction
              </Button>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        {currentStep < 10 && (
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        )}
        {currentStep > 1 && currentStep < 10 && (
          <Button variant="outline-primary" onClick={() => setCurrentStep(prev => prev - 1)}>
            Previous
          </Button>
        )}
        {currentStep < 4 && currentStep >= 1 && (
          <Button 
            variant="primary" 
            onClick={() => {
              if (currentStep === 1 && remitterData.mobile) {
                // If on step 1 and mobile is entered, try query first
                handleQueryRemitter();
              } else if (currentStep === 2 && remitterData.ekyc_id && remitterData.stateresp) {
                // If on step 2 and E-KYC is done, go to step 3
                setCurrentStep(3);
              } else if (currentStep === 3 && remitterData.ekyc_id && remitterData.stateresp) {
                // If on step 3 and registration data is ready, go to step 4
                setCurrentStep(4);
                fetchBeneficiaries(false); // Silent fetch
              }
            }}
            disabled={loading}
          >
            Next
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ComprehensiveDMTModal;
